import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from 'react-query';
import { getRoles, getDirectoryUsers, createUser, getUsers } from '../../services/iam';
import { Role, DirectoryUser } from '../../types/iam';
import { toast } from '@/lib/toast';
import { Check, ChevronDown, UserPlus } from 'lucide-react';
import { Button, CardButton, InitialsAvatar, Modal, SearchField, Select, Spinner } from '@/components/ds-zta';

interface Props {
  onClose: () => void;
}

const PAGE_SIZE = 20;

const fullName = (u: DirectoryUser) => `${u.firstName || ''} ${u.lastName || ''}`.trim();

const AddUserModal: React.FC<Props> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<DirectoryUser | null>(null);
  const [selectedRole, setSelectedRole] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Directory users, paginated. Search and sort are optional — the first page
  // loads as soon as the dropdown opens, and more pages fetch on scroll.
  const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery(
    ['directory-users', search],
    ({ pageParam = 1 }) =>
      getDirectoryUsers({ search: search.trim() || undefined, pageNo: pageParam, pageSize: PAGE_SIZE }),
    {
      enabled: open,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        const { pageNumber, totalPages } = lastPage.data;
        return pageNumber < totalPages ? pageNumber + 1 : undefined;
      },
    }
  );

  const { data: rolesResponse } = useQuery(['roles', 'primary'], () => getRoles({ limit: 100, primary_only: true }));
  const roles: Role[] = rolesResponse?.data || [];

  // Existing active users — excluded from the directory list so we don't offer to
  // add someone who is already provisioned.
  const { data: existingResponse } = useQuery(['users', 'active', 'all'], () =>
    getUsers({ status: 'active', limit: 1000, offset: 0 })
  );
  const existingEmails = useMemo(
    () => new Set((existingResponse?.data || []).map((u) => (u.email || '').toLowerCase())),
    [existingResponse]
  );

  const directoryUsers: DirectoryUser[] = useMemo(
    () =>
      (data?.pages.flatMap((p) => p.data?.content || []) || []).filter(
        (u) => !existingEmails.has((u.email || '').toLowerCase())
      ),
    [data, existingEmails]
  );

  // Close the dropdown when clicking outside of it.
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Infinite scroll: load the next page as the list nears the bottom.
  const handleScroll = () => {
    const el = listRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
      fetchNextPage();
    }
  };

  const mutation = useMutation(
    () => {
      if (!selectedUser) throw new Error('No user selected');
      const name = fullName(selectedUser);
      return createUser({
        email: selectedUser.email,
        name: name || selectedUser.email,
        primary_role: selectedRole,
        external_id: selectedUser.id,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('User added successfully');
        onClose();
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.error || `Failed to add user: ${err.message}`);
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return toast.error('Please select a user');
    if (!selectedRole) return toast.error('Please select a role');
    mutation.mutate();
  };

  return (
    <Modal
      open
      title="Add User"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-user-form"
            loading={mutation.isLoading}
            leadingIcon={mutation.isLoading ? undefined : <UserPlus className="h-4 w-4" />}
            disabled={mutation.isLoading || !selectedUser || !selectedRole}
          >
            {mutation.isLoading ? 'Adding…' : 'Add User'}
          </Button>
        </>
      }
    >
      <form id="add-user-form" onSubmit={handleSubmit} className="space-y-5 text-[var(--d2d-text-primary)]">
        <p className="-mt-2 text-sm text-[var(--d2d-text-muted)]">
          Select a user from your directory and assign a role to grant them access.
        </p>

        {/* Email (directory user) — searchable, paginated dropdown */}
        <div ref={containerRef} className="relative">
          <label className="block text-sm font-medium text-[var(--d2d-text-secondary)]">User</label>
          <CardButton
            onClick={() => setOpen((o) => !o)}
            className="mt-1.5 flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--ds-color-border-strong)] bg-[var(--d2d-bg-card)] px-3 py-2.5 text-left text-sm transition-colors hover:border-[var(--ds-color-border-strong)] hover:bg-[var(--ds-color-bg-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--ds-color-ring)]"
          >
            {selectedUser ? (
              <span className="flex min-w-0 items-center gap-2.5">
                <InitialsAvatar size="sm" name={fullName(selectedUser)} email={selectedUser.email} />
                <span className="min-w-0">
                  <span className="block truncate font-medium">{fullName(selectedUser) || selectedUser.email}</span>
                  <span className="block truncate text-xs text-[var(--d2d-text-muted)]">{selectedUser.email}</span>
                </span>
              </span>
            ) : (
              <span className="text-[var(--d2d-text-muted)]">Select a user…</span>
            )}
            <ChevronDown className={`h-4 w-4 flex-shrink-0 text-[var(--d2d-text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
          </CardButton>

          {open && (
            <div className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-lg border border-[var(--d2d-border)] bg-[var(--d2d-bg-card)] shadow-lg">
              <div className="flex items-center gap-2 border-b border-[var(--d2d-border)] px-3 py-2">
                <SearchField
                  className="flex-1"
                  size="sm"
                  label="Search users"
                  placeholder="Search users (optional)…"
                  value={search}
                  onChange={setSearch}
                  autoFocus
                />
                {isFetching && !isFetchingNextPage && <Spinner size={16} label="Searching users" className="text-[var(--d2d-text-muted)]" />}
              </div>

              <div ref={listRef} onScroll={handleScroll} className="max-h-64 overflow-y-auto py-1">
                {directoryUsers.length === 0 && !isFetching ? (
                  <div className="px-3 py-6 text-center text-sm text-[var(--d2d-text-muted)]">
                    No users available to add
                  </div>
                ) : (
                  directoryUsers.map((u) => {
                    const isSelected = selectedUser?.id === u.id;
                    const name = fullName(u);
                    return (
                      <CardButton
                        key={u.id}
                        onClick={() => {
                          setSelectedUser(u);
                          setOpen(false);
                        }}
                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-[var(--ds-color-bg-hover)] ${
                          isSelected ? 'bg-[var(--ds-color-bg-subtle)]' : ''
                        }`}
                      >
                        <InitialsAvatar size="md" name={name} email={u.email} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{name || u.email}</span>
                          <span className="block truncate text-xs text-[var(--d2d-text-muted)]">{u.email}</span>
                        </span>
                        {isSelected && <Check className="h-4 w-4 flex-shrink-0 text-[var(--d2d-text-primary)]" />}
                      </CardButton>
                    );
                  })
                )}
                {isFetchingNextPage && (
                  <div className="flex justify-center py-2">
                    <Spinner size={16} label="Loading more users" className="text-[var(--d2d-text-muted)]" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Role */}
        <Select
          id="primary_role"
          name="primary_role"
          label="Role"
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
        >
          <option value="">Select a role…</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </Select>
      </form>
    </Modal>
  );
};

export default AddUserModal;
